import React, { useImperativeHandle, useMemo, useRef, useState } from 'react';
import Lightbox, {
  ControllerRef,
  SlotStyles,
} from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import { Button } from '@/components/UI/Button/Button';
import { CloseIcon } from '@/components/UI/Icons/Icons';
import { useGalleryStore } from '@/store/useGalleryStore';

export interface GalleryRef {
  open: () => void;
  close: () => void;
}

interface GalleryProps {
  images: string[];
  sliderId: string;
  rootStyles?: React.CSSProperties;
  isSimpleView?: boolean;
  onClose?: () => void;
}

const Gallery = React.forwardRef<GalleryRef, GalleryProps>(
  (
    { images, sliderId, rootStyles, isSimpleView = true, onClose },
    galleryRef
  ) => {
    const { currentIndex, setCurrentIndex } = useGalleryStore();
    const controllerRef = useRef<ControllerRef>(null);
    const zoomRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);

    function close() {
      setIsOpen(false);
      controllerRef.current?.close();
    }

    useImperativeHandle(
      galleryRef,
      () => ({
        open: () => {
          setIsOpen(true);
        },
        close,
      }),
      []
    );

    const renderProps = {
      iconZoomIn: () => null,
      iconZoomOut: () => null,
      buttonPrev: () => null,
      buttonNext: () => null,
      buttonClose: () =>
        isSimpleView ? (
          <Button key="buttonClose" appearance="transparent" onClick={close}>
            <CloseIcon fill="#fff" />
          </Button>
        ) : null,
    };

    const styles = {
      root: { position: 'fixed', zIndex: 100001, ...rootStyles },
      slide: { padding: 0 },
    } as SlotStyles;

    const portalProps = {
      root:
        typeof document !== 'undefined'
          ? document.querySelector(`#${sliderId}`)
          : null,
    };

    const controllerProps = {
      ref: controllerRef,
      closeOnPullUp: true,
      closeOnPullDown: true,
    };

    const zoomProps = {
      ref: zoomRef,
      doubleClickMaxStops: 1,
      scrollToZoom: true,
    };

    const slides = useMemo(() => images.map(el => ({ src: el })), [images]);

    return (
      <div>
        <Lightbox
          on={{
            view: ({ index }) => {
              setCurrentIndex(index);
            },
            exited: () => onClose?.(),
          }}
          index={currentIndex}
          open={isOpen}
          styles={styles}
          render={renderProps}
          controller={controllerProps}
          slides={slides}
          plugins={[Zoom]}
          portal={portalProps}
          animation={{ fade: 0 }}
          zoom={zoomProps}
        />
      </div>
    );
  }
);

Gallery.displayName = 'Gallery';

export default React.memo(Gallery);
